import React, { useMemo, useRef, useState } from 'react';
import api from './api';
import { analyserImport, EXEMPLE_IMPORT } from './importTaches';
import { PRIORITY_LABELS, STATUS_LABELS, userDisplayName, useFermerAvecEchap } from './helpers';

/**
 * Import de tâches en lot.
 *
 * Le principe : on colle une liste, on voit un aperçu de ce qui va être créé,
 * puis on valide. L'aperçu est le point important — créer trente tâches sans
 * les avoir relues serait pire que de les saisir une par une.
 */
function ImportTasksModal({ project, users, onClose, onImported }) {
  useFermerAvecEchap(onClose);
  const [texte, setTexte] = useState('');
  const [erreurServeur, setErreurServeur] = useState('');
  const [detailsErreur, setDetailsErreur] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const champFichier = useRef(null);

  const { taches, erreurs } = useMemo(() => analyserImport(texte), [texte]);

  const nombreItems = taches.reduce((n, t) => n + t.checklist.length, 0);

  // On signale les responsables inconnus avant l'envoi : le serveur les
  // refuserait, autant le voir tout de suite.
  const inconnus = useMemo(() => {
    const noms = new Set();
    taches.forEach((t) => {
      if (!t.assigned_to) return;
      const recherche = t.assigned_to.toLowerCase();
      const trouve = users.find(
        (u) => String(u.id) === recherche || u.username.toLowerCase() === recherche
      );
      if (!trouve) noms.add(t.assigned_to);
    });
    return [...noms];
  }, [taches, users]);

  const chargerFichier = (e) => {
    const fichier = e.target.files?.[0];
    if (!fichier) return;
    const lecteur = new FileReader();
    lecteur.onload = () => setTexte(String(lecteur.result || ''));
    lecteur.readAsText(fichier, 'UTF-8');
  };

  const importer = () => {
    setErreurServeur('');
    setDetailsErreur([]);
    setEnvoi(true);

    api
      .post('/tasks/import', {
        project_id: project.id,
        tasks: taches.map((t) => ({
          name: t.name,
          description: t.description,
          start_date: t.start_date,
          due_date: t.due_date,
          priority: t.priority,
          status: t.status,
          assigned_to: t.assigned_to,
          checklist: t.checklist,
        })),
      })
      .then((res) => {
        if (onImported) onImported(res.data);
      })
      .catch((err) => {
        setErreurServeur(err.response?.data?.message || "L'import a échoué.");
        setDetailsErreur(err.response?.data?.errors || []);
      })
      .finally(() => setEnvoi(false));
  };

  const nomResponsable = (valeur) => {
    if (!valeur) return '—';
    const recherche = valeur.toLowerCase();
    const trouve = users.find(
      (u) => String(u.id) === recherche || u.username.toLowerCase() === recherche
    );
    return trouve ? userDisplayName(trouve) : valeur;
  };

  const pretAImporter = taches.length > 0 && erreurs.length === 0 && inconnus.length === 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Importer des tâches — {project.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <p className="import-aide">
          Collez votre liste ci-dessous, une tâche par ligne. Les champs sont séparés
          par une barre verticale <code>|</code> et seul le nom est obligatoire :
          <br />
          <code>Nom | début | échéance | priorité | responsable</code>
          <br />
          Une ligne commençant par <code>-</code> ajoute un élément de checklist à la
          tâche du dessus, une ligne commençant par <code>&gt;</code> lui ajoute une
          description. Les lignes vides et celles commençant par <code>{'//'}</code> sont ignorées.
        </p>

        <div className="form-group">
          <label>Liste des tâches</label>
          <textarea
            className="import-zone"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={EXEMPLE_IMPORT}
            spellCheck={false}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" onClick={() => champFichier.current?.click()}>
            Charger un fichier texte
          </button>
          <input
            ref={champFichier}
            type="file"
            accept=".txt,.csv,.md"
            style={{ display: 'none' }}
            onChange={chargerFichier}
          />
          {texte && (
            <button className="btn btn-sm" onClick={() => setTexte('')}>Vider</button>
          )}
          {!texte && (
            <button className="btn btn-sm" onClick={() => setTexte(EXEMPLE_IMPORT)}>
              Insérer un exemple
            </button>
          )}
        </div>

        {erreurs.length > 0 && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            <strong>{erreurs.length} problème(s) dans la liste :</strong>
            <ul style={{ margin: '6px 0 0 18px' }}>
              {erreurs.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {inconnus.length > 0 && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            Responsable(s) inconnu(s) : {inconnus.join(', ')}. Utilisez l’identifiant
            exact du compte, par exemple <code>mohamed_saadi</code>.
          </div>
        )}

        {erreurServeur && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            {erreurServeur}
            {detailsErreur.length > 0 && (
              <ul style={{ margin: '6px 0 0 18px' }}>
                {detailsErreur.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}

        {taches.length > 0 && (
          <>
            <h3 style={{ fontSize: 13, margin: '4px 0 8px' }}>
              Aperçu — {taches.length} tâche(s)
              {nombreItems > 0 && ` et ${nombreItems} élément(s) de checklist`}
            </h3>
            <div className="import-apercu">
              <table className="rapport-tableau">
                <thead>
                  <tr>
                    <th>Tâche</th>
                    <th>Début</th>
                    <th>Échéance</th>
                    <th>Priorité</th>
                    <th>Statut</th>
                    <th>Responsable</th>
                    <th className="rapport-col-droite">Checklist</th>
                  </tr>
                </thead>
                <tbody>
                  {taches.map((t, i) => (
                    <tr key={i}>
                      <td>{t.name}</td>
                      <td>{t.start_date || '—'}</td>
                      <td>{t.due_date || '—'}</td>
                      <td>{PRIORITY_LABELS[t.priority]}</td>
                      <td>{STATUS_LABELS[t.status]}</td>
                      <td>{nomResponsable(t.assigned_to)}</td>
                      <td className="rapport-col-droite">{t.checklist.length || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={importer}
            disabled={!pretAImporter || envoi}
          >
            {envoi
              ? 'Import en cours...'
              : `Importer ${taches.length || ''} tâche${taches.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportTasksModal;
